import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../components/ui/sheet';
import { Search, ShoppingCart, User, Menu, ChevronDown, ChevronRight, Phone, Mail, Shield, Globe, X } from 'lucide-react';
import ObfuscatedEmail from './ObfuscatedEmail';

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount } = useCart();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [navMenus, setNavMenus] = useState([]);
  const [logoUrl, setLogoUrl] = useState('https://customer-assets.emergentagent.com/job_199fcec8-3b1b-464c-9d1a-a3d18387839e/artifacts/z6e4jlgs_PZV%20noir%20transp.png');
  const [topPhone, setTopPhone] = useState('+33 1 45 85 81 76');
  const [topEmail, setTopEmail] = useState('planning@puzzlevideo.fr');
  const [openAccordion, setOpenAccordion] = useState(null);
  const [openMenuIdx, setOpenMenuIdx] = useState(null);
  const [slideDir, setSlideDir] = useState('none');

  const closeTimer = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [menuRes, settingsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu/hierarchy`).then(r => r.json()),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`).then(r => r.json())
        ]);
        // The menu API now returns `name_en` alongside `name` for every
        // node — pick the right one based on the current language, falling
        // back to French when a translation is missing.
        const isEn = i18n.language === 'en';
        const pickName = (o) => isEn ? (o?.name_en || o?.name) : (o?.name);
        const menus = (menuRes.menus || []).map(m => ({
          label: pickName(m),
          children: (m.children || []).map(sub => ({
            name: pickName(sub),
            grandchildren: (sub.children || []).map(gc => ({
              name: typeof gc === 'string' ? gc : pickName(gc),
            })).sort((a, b) => a.name.localeCompare(b.name, isEn ? 'en' : 'fr'))
          }))
        }));
        setNavMenus(menus);
        if (settingsRes?.appearance?.logo_url) setLogoUrl(settingsRes.appearance.logo_url);
        if (settingsRes?.appearance?.footer_phone) setTopPhone(settingsRes.appearance.footer_phone);
        if (settingsRes?.appearance?.footer_email) setTopEmail(settingsRes.appearance.footer_email);
        // Dynamically update favicon if admin uploaded a custom one
        const faviconUrl = settingsRes?.appearance?.favicon_url;
        if (faviconUrl) {
          // Remove all existing icon-related <link> tags then add a single one pointing to the admin-defined favicon
          document.querySelectorAll('link[rel*="icon"]').forEach(l => l.remove());
          const link = document.createElement('link');
          link.rel = 'icon';
          link.href = faviconUrl;
          document.head.appendChild(link);
          // Apple touch icon too (iOS home screen)
          const apple = document.createElement('link');
          apple.rel = 'apple-touch-icon';
          apple.href = faviconUrl;
          document.head.appendChild(apple);
        }
      } catch (e) { console.error('Header fetch error:', e); }
    };
    fetchData();
    // Re-run whenever the language changes so the mega-menu labels update
    // without a full page reload.
  }, [i18n.language]);

  const handleMenuEnter = useCallback((idx) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openMenuIdx !== null && openMenuIdx !== idx) {
      setSlideDir(idx > openMenuIdx ? 'right' : 'left');
    } else {
      setSlideDir('none');
    }
    setOpenMenuIdx(idx);
  }, [openMenuIdx]);

  const handleMenuLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => { setOpenMenuIdx(null); setSlideDir('none'); }, 150);
  }, []);

  const handlePanelEnter = useCallback(() => { if (closeTimer.current) clearTimeout(closeTimer.current); }, []);
  const handlePanelLeave = useCallback(() => {
    closeTimer.current = setTimeout(() => { setOpenMenuIdx(null); setSlideDir('none'); }, 150);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/catalogue?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchExpanded(false);
    }
  };

  const openSearch = () => {
    setSearchExpanded(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    });
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border" data-testid="header">
      {/* Top bar */}
      <div className="bg-black text-white text-[10px]">
        <div className="container mx-auto px-4 md:px-8 lg:px-16 py-1 flex items-center justify-between">
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-center">
            <span className="hidden sm:inline">{t('footer.tagline')}</span>
            <span className="hidden sm:inline text-white/40">|</span>
            <a href={`tel:${topPhone.replace(/[^+0-9]/g, '')}`} className="flex items-center gap-1 hover:text-primary transition-colors">
              <Phone className="w-2.5 h-2.5" />
              {topPhone}
            </a>
            {topEmail && (
              <>
                <span className="hidden sm:inline text-white/40">|</span>
                <span className="hidden sm:flex items-center gap-1">
                  <Mail className="w-2.5 h-2.5" />
                  <ObfuscatedEmail
                    email={topEmail}
                    className="hover:text-primary transition-colors underline-offset-2 hover:underline"
                    testId="header-email"
                    revealLabel="Afficher l’email"
                  />
                </span>
              </>
            )}
          </div>
          <div className="flex-1 flex justify-end">
            <button
              onClick={() => { const nl = i18n.language === 'fr' ? 'en' : 'fr'; i18n.changeLanguage(nl); localStorage.setItem('pzv_lang', nl); }}
              className="flex items-center gap-1.5 hover:text-primary transition-colors min-h-[40px] min-w-[40px] justify-center px-1.5 py-1"
              data-testid="lang-switch"
              aria-label={i18n.language === 'fr' ? 'Switch to English' : 'Passer en français'}
              title={i18n.language === 'fr' ? 'Switch to English' : 'Passer en français'}
            >
              {/* Flag emoji renders natively on all modern OS; falls back to text if missing */}
              <span className="text-sm leading-none" aria-hidden="true">{i18n.language === 'fr' ? '🇬🇧' : '🇫🇷'}</span>
              <span className="uppercase font-semibold text-[10px] tracking-wider">{i18n.language === 'fr' ? 'EN' : 'FR'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main row */}
      <div className="container mx-auto px-4 md:px-8 lg:px-16 py-2">
        <div className="flex items-center justify-between gap-2">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img src={logoUrl} alt="Puzzle Video" width="200" height="110" loading="eager" decoding="async" className="h-10 w-auto" />
          </Link>

          {/* Desktop nav — hidden when search expanded */}
          <nav className={`hidden lg:flex items-center gap-0.5 flex-1 justify-center transition-all duration-300 ${searchExpanded ? 'opacity-0 pointer-events-none w-0 overflow-hidden' : 'opacity-100'}`} data-testid="main-nav">
            {navMenus.map((menu, idx) => (
              <div key={menu.label} className="relative" onMouseEnter={() => handleMenuEnter(idx)} onMouseLeave={handleMenuLeave}>
                <Link
                  to={`/catalogue?nav_menu=${encodeURIComponent(menu.label)}`}
                  className={`flex items-center gap-0.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-2 whitespace-nowrap transition-colors ${openMenuIdx === idx ? 'text-primary' : 'hover:text-primary'}`}
                  data-testid={`nav-${menu.label.toLowerCase().replace(/\s+/g, '-').replace(/[éè]/g, 'e')}`}
                >
                  {menu.label}
                  {menu.children.length > 0 && <ChevronDown className={`w-2.5 h-2.5 transition-transform duration-200 ${openMenuIdx === idx ? 'rotate-180 opacity-100' : 'opacity-50'}`} />}
                </Link>
              </div>
            ))}
          </nav>

          {/* Search: icon only → expands with rounded field */}
          <div className={`hidden md:flex items-center transition-all duration-300 ${searchExpanded ? 'flex-1 mx-4' : ''}`}>
            {searchExpanded ? (
              <form onSubmit={handleSearch} className="flex items-center w-full bg-gray-100 rounded-full px-4 py-1.5 focus-within:ring-1 focus-within:ring-primary transition-all">
                <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <input ref={searchInputRef} type="text" placeholder={t('nav.search')} value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)} autoFocus
                  className="flex-1 bg-transparent text-sm outline-none border-none ml-2 placeholder:text-muted-foreground"
                  data-testid="search-expanded-input" />
                <button type="button" onClick={() => { setSearchExpanded(false); setSearchQuery(''); }}
                  className="p-1 hover:text-primary transition-colors"><X className="w-4 h-4" /></button>
              </form>
            ) : (
              <button onClick={openSearch} className="p-2 hover:text-primary transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center" data-testid="search-trigger">
                <Search className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5">
            <Link to="/panier" className="relative p-2 hover:text-primary transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && <span className="absolute -top-1 -right-1 bg-primary text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{itemCount}</span>}
            </Link>

            {isAuthenticated ? (
              <div className="hidden md:flex items-center gap-1">
                {user?.role === 'admin' && (
                  <Link to="/admin" className="flex items-center gap-1 p-2 text-primary hover:text-primary/80 transition-colors" data-testid="admin-link">
                    <Shield className="w-4 h-4" /><span className="text-xs font-semibold">Admin</span>
                  </Link>
                )}
                <Link to="/compte" className="flex items-center gap-1 p-2 hover:text-primary transition-colors" data-testid="account-link">
                  <User className="w-4 h-4" /><span className="text-xs">{user?.name?.split(' ')[0]}</span>
                </Link>
                <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn" className="text-xs h-8 px-2">{t('nav.logout')}</Button>
              </div>
            ) : (
              <Link to="/connexion" className="hidden md:block" data-testid="login-link">
                <Button variant="outline" size="sm" className="rounded-none text-xs h-8">{t('nav.login')}</Button>
              </Link>
            )}

            {/* Mobile */}
            <div className="md:hidden flex items-center">
              <button onClick={openSearch} className="p-2 hover:text-primary transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center" data-testid="mobile-search-trigger">
                <Search className="w-5 h-5" />
              </button>
            </div>
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="min-h-[40px] min-w-[40px]"><Menu className="w-5 h-5" /></Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] flex flex-col p-0">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="p-4 border-b">
                  <form onSubmit={(e) => { handleSearch(e); setMobileMenuOpen(false); }} className="flex items-center bg-gray-100 rounded-full px-3 py-1.5">
                    <Search className="w-4 h-4 text-muted-foreground" />
                    <input type="text" placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                      className="flex-1 bg-transparent text-sm outline-none ml-2" data-testid="mobile-search-input" />
                  </form>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-1">
                    {navMenus.map((menu) => (
                      <div key={menu.label} className="border-b border-border/50 last:border-0">
                        <button onClick={() => setOpenAccordion(prev => prev === menu.label ? null : menu.label)}
                          className="flex items-center justify-between w-full py-3 text-left text-sm font-semibold uppercase tracking-wide hover:text-primary transition-colors">
                          {menu.label}
                          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openAccordion === menu.label ? 'rotate-180' : ''}`} />
                        </button>
                        {openAccordion === menu.label && (
                          <div className="pb-3 pl-3 space-y-1 animate-in slide-in-from-top-1 duration-150">
                            <Link to={`/catalogue?nav_menu=${encodeURIComponent(menu.label)}`} className="block text-xs font-semibold text-primary py-1" onClick={() => setMobileMenuOpen(false)}>Tout voir</Link>
                            {(menu.children || []).map((sub) => (
                              <div key={sub.name}>
                                <Link to={`/catalogue?nav_menu=${encodeURIComponent(menu.label)}&nav_sous_menu=${encodeURIComponent(sub.name)}`}
                                  className="block text-sm font-medium text-foreground hover:text-primary transition-colors py-1" onClick={() => setMobileMenuOpen(false)}>{sub.name}</Link>
                                {sub.grandchildren?.length > 0 && (
                                  <div className="pl-3 space-y-0.5">
                                    {sub.grandchildren.map((gc) => (
                                      <Link key={gc.name} to={`/catalogue?nav_menu=${encodeURIComponent(menu.label)}&nav_sous_menu=${encodeURIComponent(sub.name)}&nav_sous_sous_menu=${encodeURIComponent(gc.name)}`}
                                        className="block text-xs text-muted-foreground hover:text-primary transition-colors py-0.5" onClick={() => setMobileMenuOpen(false)}>{gc.name}</Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                            {(menu.children || []).some(s => s.name === 'GRIP') && (
                              <Link to="/kit-grip" className="block text-xs font-semibold text-primary mt-2 py-1 border-t border-border/30 pt-2" onClick={() => setMobileMenuOpen(false)}><span className="inline-flex items-center font-black italic tracking-tight text-primary">&#9889;FASTGRIP</span></Link>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-4 border-t mt-auto">
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <Link to="/compte" className="block font-medium" onClick={() => setMobileMenuOpen(false)}>{t('nav.account')}</Link>
                      <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-muted-foreground hover:text-primary transition-colors">{t('nav.logout')}</button>
                    </div>
                  ) : (
                    <Link to="/connexion" onClick={() => setMobileMenuOpen(false)}><Button className="w-full">{t('nav.login')}</Button></Link>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Mobile expanded search bar — shown outside burger menu */}
      {searchExpanded && (
        <div className="md:hidden px-4 pb-2 bg-white border-b border-border">
          <form onSubmit={handleSearch} className="flex items-center bg-gray-100 rounded-full px-3 py-1.5 focus-within:ring-1 focus-within:ring-primary transition-all">
            <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <input ref={searchInputRef} type="text" placeholder={t('nav.search')} value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} autoFocus
              className="flex-1 bg-transparent text-sm outline-none border-none ml-2 placeholder:text-muted-foreground"
              data-testid="mobile-search-expanded" />
            <button type="button" onClick={() => { setSearchExpanded(false); setSearchQuery(''); }}
              className="p-1 hover:text-primary transition-colors"><X className="w-3 h-3" /></button>
          </form>
        </div>
      )}

      {/* Mega-menu panels */}
      {openMenuIdx !== null && navMenus[openMenuIdx]?.children.length > 0 && (
        <div className="absolute left-0 right-0 bg-white border-t border-border shadow-xl z-50"
          onMouseEnter={handlePanelEnter} onMouseLeave={handlePanelLeave} data-testid="mega-menu-panel"
          style={{ maxHeight: '450px', top: '100%' }}>
          <div className="mx-auto px-4 lg:px-8 py-4 overflow-y-auto" style={{ maxHeight: '430px',
            animation: slideDir === 'right' ? 'slideFromRight 0.35s ease-out' : slideDir === 'left' ? 'slideFromLeft 0.35s ease-out' : 'fadeIn 0.2s ease-out'
          }} key={openMenuIdx}>
            <div className="flex justify-center gap-x-4">
              {navMenus[openMenuIdx].children.map((subCat) => (
                <div key={subCat.name} className="min-w-0" style={{ width: `${Math.min(100 / navMenus[openMenuIdx].children.length, 22)}%` }}>
                  <Link to={`/catalogue?nav_menu=${encodeURIComponent(navMenus[openMenuIdx].label)}&nav_sous_menu=${encodeURIComponent(subCat.name)}`}
                    className="text-[11px] font-bold uppercase tracking-wide text-foreground hover:text-primary transition-colors block pb-1.5 mb-1.5 border-b border-primary/20 truncate"
                    onClick={() => setOpenMenuIdx(null)} title={subCat.name}>{subCat.name}</Link>
                  {subCat.grandchildren?.length > 0 && (
                    <ul className="space-y-0.5">
                      {subCat.grandchildren.map((gc) => (
                        <li key={gc.name}>
                          <Link to={`/catalogue?nav_menu=${encodeURIComponent(navMenus[openMenuIdx].label)}&nav_sous_menu=${encodeURIComponent(subCat.name)}&nav_sous_sous_menu=${encodeURIComponent(gc.name)}`}
                            className="text-[11px] font-medium text-muted-foreground hover:text-primary transition-colors block py-0.5 leading-snug truncate"
                            onClick={() => setOpenMenuIdx(null)} title={gc.name}>{gc.name}</Link>
                        </li>
                      ))}
                      {subCat.name === 'GRIP' && (
                        <li><Link to="/kit-grip" className="text-[11px] font-bold text-primary hover:text-primary/80 transition-colors block py-1 mt-1 border-t border-primary/10"
                          onClick={() => setOpenMenuIdx(null)} data-testid="mega-grip-kit-link"><span className="inline-flex items-center font-black italic tracking-tight text-primary">&#9889;FASTGRIP</span></Link></li>
                      )}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            {/* "Voir tout" link removed per user request */}
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideFromRight { from { opacity: 0; transform: translateX(30px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes slideFromLeft { from { opacity: 0; transform: translateX(-30px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      `}</style>
    </header>
  );
};

export default Header;
